<!--
  SuperAdmin: create user.

  Card: SA Users page + per-user detail -- https://trello.com/c/tRC6K0Zn
  Backend: POST /api/superadmin/users

  The SA picks the role and the tenancy scoping. Password handling is
  the product decision worth restating at the call site:

    * Leave the password BLANK (the default) -> the account is created
      with no password and the user is emailed a set-password link.
    * Type a password -> it is set directly.

  Blank-means-invite is the default posture precisely so no SA ends up
  knowing a user's credential, so the field ships empty and the hint
  under it says what blank does -- an SA should never type a password
  merely because they are unsure what an empty box will do.

  Access: route-gated on superadmin.settings.

  ASCII-only.
-->
<template>
    <div data-testid="sa-user-create">
        <div class="sa-user__header">
            <h1 class="sa-page-title">New user</h1>
            <button
                type="button"
                class="sa-btn"
                data-testid="sa-user-new-cancel"
                @click="$router.push('/admin/users')"
            >
                Cancel
            </button>
        </div>

        <p
            v-if="errorMsg"
            class="sa-banner sa-banner--error"
            data-testid="sa-user-new-error"
        >
            {{ errorMsg }}
        </p>
        <p
            v-if="successMsg"
            class="sa-banner sa-banner--ok"
            data-testid="sa-user-new-success"
        >
            {{ successMsg }}
        </p>

        <section class="sa-card">
            <h2 class="sa-card__title">Identity</h2>

            <label class="sa-field">
                <span>Username</span>
                <input
                    class="sa-input"
                    data-testid="sa-user-new-username"
                    :value="form.username"
                    @input="onUsernameInput"
                />
            </label>
            <p
                v-if="usernameTaken"
                class="sa-warn"
                data-testid="sa-user-new-username-taken"
            >
                That username is taken.
            </p>

            <label class="sa-field">
                <span>Email</span>
                <input
                    class="sa-input"
                    type="email"
                    data-testid="sa-user-new-email"
                    v-model="form.email"
                />
            </label>

            <label class="sa-field">
                <span>First name</span>
                <input
                    class="sa-input"
                    data-testid="sa-user-new-first-name"
                    v-model="form.first_name"
                />
            </label>

            <label class="sa-field">
                <span>Last name</span>
                <input
                    class="sa-input"
                    data-testid="sa-user-new-last-name"
                    v-model="form.last_name"
                />
            </label>
        </section>

        <section class="sa-card">
            <h2 class="sa-card__title">Role and tenancy</h2>

            <label class="sa-field">
                <span>Role</span>
                <select
                    class="sa-input"
                    data-testid="sa-user-new-role"
                    v-model="form.role_slug"
                >
                    <option value="">Select a role</option>
                    <option
                        v-for="r in options.roles"
                        :key="r.id"
                        :value="r.slug"
                    >
                        {{ r.name }}
                    </option>
                </select>
            </label>

            <label class="sa-field">
                <span>Distributor</span>
                <select
                    class="sa-input"
                    data-testid="sa-user-new-distributor"
                    v-model="form.distributor_id"
                >
                    <option value="">None</option>
                    <option
                        v-for="d in options.distributors"
                        :key="d.id"
                        :value="String(d.id)"
                    >
                        {{ d.name }}
                    </option>
                </select>
            </label>

            <label class="sa-field">
                <span>Client</span>
                <select
                    class="sa-input"
                    data-testid="sa-user-new-client"
                    v-model="form.client_id"
                >
                    <option value="">None</option>
                    <option
                        v-for="c in options.clients"
                        :key="c.id"
                        :value="String(c.id)"
                    >
                        {{ c.name }}
                    </option>
                </select>
            </label>
        </section>

        <section class="sa-card">
            <h2 class="sa-card__title">Password</h2>
            <label class="sa-field">
                <span>Password</span>
                <input
                    class="sa-input"
                    type="password"
                    autocomplete="new-password"
                    data-testid="sa-user-new-password"
                    v-model="form.password"
                />
            </label>
            <p class="sa-muted" data-testid="sa-user-new-password-hint">
                Leave blank to email the user a set-password link. This is
                the recommended option -- it avoids anyone else knowing
                their password. A password set here must be at least 8
                characters and include a number, an upper-case letter, or a
                symbol.
            </p>
        </section>

        <div class="sa-actions">
            <button
                type="button"
                class="sa-btn sa-btn--primary"
                data-testid="sa-user-new-save"
                :disabled="saving"
                @click="save"
            >
                Create user
            </button>
        </div>
    </div>
</template>

<script>
import {
    createUser,
    getFilterOptions,
    checkUsernameAvailable,
} from "@/services/superadminUsersService";
import { validatePassword } from "@/utils/passwordPolicy";

const USERNAME_DEBOUNCE_MS = 300;

export default {
    name: "SuperAdminUserCreate",
    data() {
        return {
            saving: false,
            errorMsg: "",
            successMsg: "",
            options: { distributors: [], clients: [], roles: [] },
            form: {
                username: "",
                email: "",
                first_name: "",
                last_name: "",
                role_slug: "",
                distributor_id: "",
                client_id: "",
                password: "",
            },
            usernameTaken: false,
            usernameTimer: null,
        };
    },
    async mounted() {
        // `?distributor_id=N` prefill. The distributor pages link here as
        // "add an account for THIS distributor" (a link that predates
        // this page and is preserved through the /admin/accounts/add
        // redirect), so honour the param rather than making the operator
        // re-pick a distributor they already chose.
        const q = (this.$route && this.$route.query) || {};
        if (q.distributor_id) {
            this.form.distributor_id = String(q.distributor_id);
        }

        try {
            const resp = await getFilterOptions();
            const d = resp && resp.data ? resp.data : resp;
            this.options = {
                distributors: (d && d.distributors) || [],
                clients: (d && d.clients) || [],
                roles: (d && d.roles) || [],
            };
        } catch (e) {
            this.errorMsg =
                "Could not load roles and tenancy options. Please reload.";
        }
    },
    beforeUnmount() {
        if (this.usernameTimer) {
            clearTimeout(this.usernameTimer);
        }
    },
    methods: {
        onUsernameInput(event) {
            this.form.username = event.target.value;
            if (this.usernameTimer) {
                clearTimeout(this.usernameTimer);
            }
            const candidate = this.form.username;
            if (!candidate) {
                this.usernameTaken = false;
                return;
            }
            this.usernameTimer = setTimeout(async () => {
                try {
                    const resp = await checkUsernameAvailable(candidate);
                    const data = resp && resp.data ? resp.data : resp;
                    if (candidate === this.form.username) {
                        this.usernameTaken = data && data.available === false;
                    }
                } catch (e) {
                    // Availability is a UX nicety; the server constraint
                    // is the guard.
                    this.usernameTaken = false;
                }
            }, USERNAME_DEBOUNCE_MS);
        },

        async save() {
            this.errorMsg = "";
            this.successMsg = "";

            if (!this.form.username.trim()) {
                this.errorMsg = "Username is required.";
                return;
            }
            if (!this.form.role_slug) {
                this.errorMsg = "Role is required.";
                return;
            }
            // A blank password means "email them a link", which needs a
            // mailbox. Catch it here so the operator gets a precise
            // message rather than a 422.
            if (!this.form.password && !this.form.email.trim()) {
                this.errorMsg =
                    "Email is required when no password is set, so the " +
                    "set-password link can be delivered.";
                return;
            }

            // A password set directly must meet the shared account-password
            // policy (the server enforces the same rule).
            if (this.form.password) {
                const policy = validatePassword(this.form.password);
                if (policy !== true) {
                    this.errorMsg = policy;
                    return;
                }
            }

            const payload = {
                username: this.form.username.trim(),
                email: this.form.email.trim() || null,
                first_name: this.form.first_name.trim() || null,
                last_name: this.form.last_name.trim() || null,
                role_slug: this.form.role_slug,
            };
            if (this.form.distributor_id) {
                payload.distributor_id = Number(this.form.distributor_id);
            }
            if (this.form.client_id) {
                payload.client_id = Number(this.form.client_id);
            }
            if (this.form.password) {
                payload.password = this.form.password;
            }

            this.saving = true;
            try {
                const resp = await createUser(payload);
                const data = resp && resp.data ? resp.data : resp;
                this.successMsg = data && data.set_password_email_sent
                    ? "User created. A set-password email has been sent."
                    : "User created.";
                const id = data && data.user ? data.user.id : null;
                if (id) {
                    this.$router.push(`/admin/users/${id}`);
                }
            } catch (e) {
                const msg =
                    (e &&
                        e.response &&
                        e.response.data &&
                        e.response.data.error) ||
                    (e && e.data && e.data.error);
                this.errorMsg =
                    msg || "Could not create the user. Please try again.";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.sa-user__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}
.sa-card {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 16px;
    background: #fff;
}
.sa-card__title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 12px;
    color: #2d3748;
}
.sa-field {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: #4a5568;
}
.sa-field > span {
    display: block;
    margin-bottom: 4px;
}
.sa-input {
    font-size: 13px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #fff;
    color: #2d3748;
    min-width: 260px;
}
.sa-actions {
    display: flex;
    gap: 8px;
}
.sa-btn {
    font-size: 13px;
    font-weight: 500;
    padding: 7px 14px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #2d3748;
    cursor: pointer;
}
.sa-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.sa-btn--primary {
    background: #2b6cb0;
    border-color: #2b6cb0;
    color: #fff;
}
.sa-banner {
    font-size: 13px;
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 12px;
}
.sa-banner--ok {
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    color: #22543d;
}
.sa-banner--error {
    background: #fff5f5;
    border: 1px solid #feb2b2;
    color: #c53030;
}
.sa-warn {
    font-size: 13px;
    color: #c53030;
    margin: 6px 0 10px;
}
.sa-muted {
    font-size: 12px;
    color: var(--tf-fg-4);
}
</style>
